export type AuthRole = 'customer' | 'driver' | 'admin' | 'companyOwner';

export interface AuthTokenBundle {
  accessToken?: string | null;
  refreshToken?: string | null;
  accessTokenExpiresAt?: string | null;
  refreshTokenExpiresAt?: string | null;
}

const TOKEN_KEYS: Record<AuthRole, Record<keyof Required<AuthTokenBundle>, string>> = {
  customer: {
    accessToken: 'shiftwagon_customer_access_token',
    refreshToken: 'shiftwagon_customer_refresh_token',
    accessTokenExpiresAt: 'shiftwagon_customer_access_token_expires_at',
    refreshTokenExpiresAt: 'shiftwagon_customer_refresh_token_expires_at',
  },
  driver: {
    accessToken: 'shiftwagon_driver_access_token',
    refreshToken: 'shiftwagon_driver_refresh_token',
    accessTokenExpiresAt: 'shiftwagon_driver_access_token_expires_at',
    refreshTokenExpiresAt: 'shiftwagon_driver_refresh_token_expires_at',
  },
  admin: {
    accessToken: 'shiftwagon_admin_access_token',
    refreshToken: 'shiftwagon_admin_refresh_token',
    accessTokenExpiresAt: 'shiftwagon_admin_access_token_expires_at',
    refreshTokenExpiresAt: 'shiftwagon_admin_refresh_token_expires_at',
  },
  companyOwner: {
    accessToken: 'shiftwagon_company_owner_access_token',
    refreshToken: 'shiftwagon_company_owner_refresh_token',
    accessTokenExpiresAt: 'shiftwagon_company_owner_access_token_expires_at',
    refreshTokenExpiresAt: 'shiftwagon_company_owner_refresh_token_expires_at',
  },
};

const LEGACY_TOKEN_KEYS: Partial<Record<AuthRole, string>> = {
  customer: 'shiftwagon_customer_auth_token',
  driver: 'shiftwagon_driver_auth_token',
  admin: 'shiftwagon_admin_auth_token',
  companyOwner: 'shiftwagon_company_owner_auth_token',
};

const WEB_DEVICE_ID_KEY = 'shiftwagon_web_device_id';

export function isUsableStoredToken(value?: string | null): value is string {
  const normalized = String(value ?? '').trim();
  if (!normalized) return false;

  const lowered = normalized.toLowerCase();
  return lowered !== 'undefined' && lowered !== 'null';
}

function readUsableToken(key: string): string | null {
  const value = sessionStorage.getItem(key);
  if (isUsableStoredToken(value)) {
    return value.trim();
  }

  if (value !== null) {
    sessionStorage.removeItem(key);
  }

  return null;
}

export function getAuthTokenBundle(role: AuthRole): AuthTokenBundle {
  const keys = TOKEN_KEYS[role];
  const legacyAccessToken = LEGACY_TOKEN_KEYS[role]
    ? readUsableToken(LEGACY_TOKEN_KEYS[role] as string)
    : null;

  return {
    accessToken: readUsableToken(keys.accessToken) || legacyAccessToken,
    refreshToken: readUsableToken(keys.refreshToken),
    accessTokenExpiresAt: sessionStorage.getItem(keys.accessTokenExpiresAt),
    refreshTokenExpiresAt: sessionStorage.getItem(keys.refreshTokenExpiresAt),
  };
}

export function getAccessToken(role: AuthRole): string | null {
  return getAuthTokenBundle(role).accessToken || null;
}

export function getRefreshToken(role: AuthRole): string | null {
  return getAuthTokenBundle(role).refreshToken || null;
}

export function saveAuthTokenBundle(role: AuthRole, bundle: AuthTokenBundle): void {
  const keys = TOKEN_KEYS[role];

  if (isUsableStoredToken(bundle.accessToken)) {
    sessionStorage.setItem(keys.accessToken, bundle.accessToken.trim());
    const legacyKey = LEGACY_TOKEN_KEYS[role];
    if (legacyKey) {
      sessionStorage.setItem(legacyKey, bundle.accessToken.trim());
    }
  }

  if (isUsableStoredToken(bundle.refreshToken)) {
    sessionStorage.setItem(keys.refreshToken, bundle.refreshToken.trim());
  }

  if (bundle.accessTokenExpiresAt) {
    sessionStorage.setItem(keys.accessTokenExpiresAt, bundle.accessTokenExpiresAt);
  }

  if (bundle.refreshTokenExpiresAt) {
    sessionStorage.setItem(keys.refreshTokenExpiresAt, bundle.refreshTokenExpiresAt);
  }
}

export function clearAuthTokenBundle(role: AuthRole): void {
  const keys = TOKEN_KEYS[role];
  Object.values(keys).forEach((key) => sessionStorage.removeItem(key));

  const legacyKey = LEGACY_TOKEN_KEYS[role];
  if (legacyKey) {
    sessionStorage.removeItem(legacyKey);
  }
}

export function tokenExpiresSoon(expiresAt?: string | null, skewMs = 30000): boolean {
  if (!expiresAt) return false;
  const expiryMs = Date.parse(expiresAt);
  if (Number.isNaN(expiryMs)) return false;
  return expiryMs - Date.now() <= skewMs;
}

export function getWebSessionContext() {
  let deviceId = sessionStorage.getItem(WEB_DEVICE_ID_KEY);
  if (!deviceId) {
    const randomPart =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    deviceId = `web-${randomPart}`;
    sessionStorage.setItem(WEB_DEVICE_ID_KEY, deviceId);
  }

  return {
    clientType: 'WEB',
    deviceName: navigator.userAgent ? `Web - ${navigator.userAgent.slice(0, 80)}` : 'Web browser',
    deviceId,
  };
}
